import React, { ReactNode } from 'react';
import Box from 'ui-box';
import { majorScale } from '.';
import { Select } from './Select';

import {
  Alpha2Code,
  registerLocale,
  getAlpha2Codes,
  getName,
} from 'i18n-iso-countries';
import { IntlShape, useIntl } from 'react-intl';

registerLocale(require('i18n-iso-countries/langs/en.json'));
registerLocale(require('i18n-iso-countries/langs/da.json'));

export interface ICountrySelectProps {
  value?: Alpha2Code;
  onChange?: (code: Alpha2Code) => void;
  children?: ReactNode;
  hideSearchInput?: boolean;
  isOpen?: boolean;
  onClose?: () => void;
  placeholder?: string;
}

interface CountryObject {
  id: Alpha2Code;
  code: Alpha2Code;
  name: string;
}

const codes = getAlpha2Codes();
const countryCodes: CountryObject[] = Object.keys(codes).map((key) => ({
  id: key as Alpha2Code,
  code: key as Alpha2Code,
  name: getName(key, 'da'), // TODO: Use the locale defined by the session
}));

export const getCountryName = (intl: IntlShape, code: string) => {
  return getName(code, intl.locale);
};

export const CountrySelect: React.FC<ICountrySelectProps> = ({
  value,
  onChange,
  ...props
}) => {
  const intl = useIntl();

  return (
    <Select
      isOpen={props.isOpen}
      onClose={props.onClose}
      hideSearchInput={props.hideSearchInput}
      children={props.children}
      placeholder={
        props.placeholder ||
        intl.formatMessage({
          defaultMessage: 'Country',
        })
      }
      noResultsMessage={intl.formatMessage({
        defaultMessage: 'Country not found',
      })}
      items={countryCodes}
      filterItems={(item, query) =>
        item.name.toLowerCase().indexOf(query.toLowerCase()) > -1 ||
        item.code.toLowerCase().indexOf(query.toLowerCase()) > -1
      }
      onSelect={(country) => onChange && onChange(country.code)}
      value={value || undefined}
      valueRenderer={(item: CountryObject) =>
        `${flagEmojiFromCode(item.code)} ${item.name}`
      }
      itemRenderer={(item) => (
        <>
          <p>
            {flagEmojiFromCode(item.code)} {item.name}
          </p>
          <Box
            className="p muted small font-mono"
            marginLeft="auto"
            textAlign="right"
            paddingLeft={majorScale(1)}
          >
            {item.code}
          </Box>
        </>
      )}
    />
  );
};

export const flagEmojiFromCode = (code: string) => {
  if (!codes[code]) {
    // If we do not know the code, just return.
    return '';
  }
  if (!String || !String.fromCodePoint) {
    return '';
  }
  // Magic wuuuhuh
  const MAGIC_FLAG_NUMBER = 127462 - 65;
  return String.fromCodePoint(
    ...code.split('').map((c) => MAGIC_FLAG_NUMBER + c.charCodeAt(0))
  );
};
